// src/config/sensorColorConfig.js
// =============================================================================
// Configuração ÚNICA de conversão de cor por tipo de medição.
// Usado por TODOS os consumidores (telemetria, experimento, módulos).
//
// Alinhado com a API spectral nested format (POST /convert) e validado pelo
// script simulate_api_call.py (cor correta = sem gamma, com auto-exposure).
// =============================================================================

/**
 * Configuração de defaults por measurementType.
 *
 * Estrutura espelha EXATAMENTE os sub-objetos da API (POST /convert):
 *   calibration → { type, mode }
 *   series      → { smoothing: { method, window } }
 *   output      → { chromaticAdaptation, autoExposure, luminosityCorrection, hueUnwrapped }
 *
 * Campos que NÃO existem na API (não enviar):
 *   applyGamma (frontend-only), measurementType (frontend-only)
 */
const MEASUREMENT_CONFIGS = {
    turbidimetry: {
        calibration: {
            type: 'turbidimetry',
            mode: 'spectral',
            signalQuality: {
                enabled: true,
                luminanceThreshold: 0.001,
                thresholdMode: 'absolute',
                minTristimulusSum: null,
                action: 'mask_chromaticity',
            },
        },
        series: { smoothing: { method: 'moving_average', window: 21 } },
        output: {
            chromaticAdaptation: true,
            autoExposure: false,
            luminosityCorrection: true,
            hueUnwrapped: true,
        },
    },
    nephelometry: {
        calibration: {
            type: 'nephelometry',
            mode: 'spectral',
            signalQuality: {
                enabled: true,
                luminanceThreshold: 0.15,
                thresholdMode: 'relative',
                minTristimulusSum: null,
                action: 'mask_chromaticity',
            },
        },
        series: { smoothing: { method: 'moving_average', window: 21 } },
        output: {
            chromaticAdaptation: true,
            autoExposure: true,
            luminosityCorrection: false,
            hueUnwrapped: true,
        },
    },
    fluorescence: {
        calibration: {
            type: 'fluorescence',
            mode: 'spectral',
            signalQuality: {
                enabled: true,
                luminanceThreshold: 0.15,
                thresholdMode: 'relative',
                minTristimulusSum: null,
                action: 'mask_chromaticity',
            },
        },
        series: { smoothing: { method: 'moving_average', window: 21 } },
        output: {
            chromaticAdaptation: true,
            autoExposure: false,
            luminosityCorrection: false,
            hueUnwrapped: true,
        },
    },
};

/**
 * targetColorSpaces padrão (completo).
 */
const BASE_COLOR_SPACES = ['sRGB', 'LAB', 'XYZ', 'xyY', 'HSV', 'CMYK'];

/**
 * targetColorSpaces reduzido para telemetria (tempo real) — só precisa de sRGB e xyY.
 */
const TELEMETRY_COLOR_SPACES = ['sRGB', 'xyY'];

/**
 * Retorna defaults FLAT para UI state (React useState).
 * Mapeia a estrutura nested para nomes de state usados nos componentes.
 * @param {'turbidimetry'|'nephelometry'|'fluorescence'} measurementType
 * @returns {Object} { calibrationType, conversionMode, applyChromaticAdaptation, autoExposure, applyMovingAverage, movingAverageWindow, applyLuminosityCorrection, returnHueUnwrapped }
 */
export function getMeasurementConfig(measurementType) {
    const cfg =
        MEASUREMENT_CONFIGS[measurementType] ||
        MEASUREMENT_CONFIGS.turbidimetry;
    return {
        calibrationType: cfg.calibration.type,
        conversionMode: cfg.calibration.mode,
        applyChromaticAdaptation: cfg.output.chromaticAdaptation,
        autoExposure: cfg.output.autoExposure,
        applyMovingAverage: !!cfg.series?.smoothing,
        movingAverageWindow: cfg.series?.smoothing?.window ?? 20,
        applyLuminosityCorrection: cfg.output.luminosityCorrection,
        returnHueUnwrapped: cfg.output.hueUnwrapped,
    };
}

/**
 * Monta o payload NESTED para a API de cor (POST /convert).
 *
 * O chamador ainda precisa adicionar: sensorType, rawData,
 * e calibration.referenceReading (merge posterior ou via overrides).
 *
 * @param {'turbidimetry'|'nephelometry'|'fluorescence'} measurementType
 * @param {Object} [overrides] - Sobrescritas (aceita campos flat OU nested)
 * @param {Array<string>} [overrides.targetColorSpaces] - Override de color spaces
 * @param {boolean} [overrides.applyMovingAverage] - false para desligar suavização
 * @param {number} [overrides.movingAverageWindow] - Janela da média móvel
 * @param {number} [overrides.startIndex] - Índice inicial da série
 * @param {string} [overrides.conversionMode] - Override do modo (spectral/dynamic/static)
 * @param {Object} [overrides.referenceReading] - Inserida em calibration.referenceReading
 * @param {Array} [overrides.conversionMatrix] - Inserida em calibration.conversionMatrix
 * @returns {Object} Payload nested: { targetColorSpaces, calibration, series?, output }
 */
export function buildApiConfig(measurementType, overrides = {}) {
    const cfg =
        MEASUREMENT_CONFIGS[measurementType] ||
        MEASUREMENT_CONFIGS.turbidimetry;

    // --- calibration ---
    const calibration = {
        type: cfg.calibration.type,
        mode: overrides.conversionMode || cfg.calibration.mode,
    };
    if (overrides.referenceReading) {
        calibration.referenceReading = overrides.referenceReading;
    }
    if (overrides.conversionMatrix) {
        calibration.conversionMatrix = overrides.conversionMatrix;
    }
    if (cfg.calibration.signalQuality) {
        calibration.signalQuality = { ...cfg.calibration.signalQuality };
    }
    if (overrides.signalQuality) {
        calibration.signalQuality = {
            ...(calibration.signalQuality || {}),
            ...overrides.signalQuality,
        };
    }

    // --- series ---
    const wantSmoothing =
        overrides.applyMovingAverage ?? !!cfg.series?.smoothing;
    let series;
    if (wantSmoothing) {
        const window =
            overrides.movingAverageWindow ??
            cfg.series?.smoothing?.window ??
            20;
        series = { smoothing: { method: 'moving_average', window } };
    }
    if (overrides.startIndex != null) {
        series = series || {};
        series.startIndex = overrides.startIndex;
    }

    // --- output ---
    const output = {
        chromaticAdaptation:
            overrides.applyChromaticAdaptation ??
            cfg.output.chromaticAdaptation,
        autoExposure: overrides.autoExposure ?? cfg.output.autoExposure,
        luminosityCorrection:
            overrides.applyLuminosityCorrection ??
            cfg.output.luminosityCorrection,
        hueUnwrapped: overrides.returnHueUnwrapped ?? cfg.output.hueUnwrapped,
    };
    if (overrides.chromaticityThreshold != null) {
        output.chromaticityThreshold = overrides.chromaticityThreshold;
    }

    const payload = {
        targetColorSpaces: overrides.targetColorSpaces || BASE_COLOR_SPACES,
        calibration,
        output,
    };
    if (series) {
        payload.series = series;
    }
    return payload;
}

/**
 * Versão para telemetria (tempo real): usa targetColorSpaces reduzido e sem suavização.
 */
export function buildTelemetryApiConfig(measurementType, overrides = {}) {
    return buildApiConfig(measurementType, {
        targetColorSpaces: TELEMETRY_COLOR_SPACES,
        applyMovingAverage: false,
        ...overrides,
    });
}

// Legacy alias for backward compat
const BASE_PAYLOAD = { targetColorSpaces: BASE_COLOR_SPACES };

export {
    MEASUREMENT_CONFIGS,
    BASE_PAYLOAD,
    BASE_COLOR_SPACES,
    TELEMETRY_COLOR_SPACES,
};
